import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import {
  ClockIcon,
  CurrencyEuroIcon,
  TruckIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  ChartBarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useCompanyData } from '../hooks/useCompanyData';

interface ReportFilters {
  startDate: string;
  endDate: string;
  projectId?: string;
  employeeId?: string;
}

interface TimeEntryReport {
  employee_name: string;
  project_name: string;
  total_hours: number;
  total_break_minutes: number;
  entries_count: number;
}

interface CostReport {
  project_name: string;
  category: string;
  total_amount: number;
  entries_count: number;
}

interface KilometerReport {
  employee_name: string;
  project_name: string;
  total_distance: number;
  total_cost: number;
  entries_count: number;
}

export default function Reports() {
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Roep de RPC functie aan voor de company_id
  const { data: companyId, isLoading: isLoadingCompanyId } = useQuery<string | null>({
    queryKey: ['companyId'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_current_user_company_id');
      if (error) throw new Error(`RPC Error: ${error.message}`);
      return data;
    }
  });

  const { data: companyData, isLoading: isLoadingCompanyData, isError } = useCompanyData(companyId || undefined);

  // Data voor filters
  const projects = companyData?.projects || [];
  const employees = companyData?.employees || [];

  // Client-side processing met useMemo – met optional chaining voor safety
  const timeReport = useMemo(() => {
    if (!companyData?.timeEntries) return [];

    const filtered = companyData.timeEntries.filter(entry => {
      const entryDate = new Date(entry.date);
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      return entryDate >= startDate &&
             entryDate <= endDate &&
             (!filters.projectId || entry.project_id === filters.projectId) &&
             (!filters.employeeId || entry.employee_id === filters.employeeId);
    });

    const grouped = filtered.reduce((acc: Record<string, any>, entry: any) => {
      const key = `${entry.employees?.name || 'Onbekend'}-${entry.projects?.name || 'Onbekend'}`;
      if (!acc[key]) {
        acc[key] = {
          employee_name: entry.employees?.full_name || 'Onbekend',
          project_name: entry.projects?.name || 'Onbekend',
          total_hours: 0,
          total_break_minutes: 0,
          entries_count: 0
        };
      }
      const startTime = new Date(`1970-01-01T${entry.start_time}Z`);
      const endTime = new Date(`1970-01-01T${entry.end_time}Z`);
      let hours = (endTime.getTime() - startTime.getTime()) / 3600000;
      if (hours < 0) hours += 24;
      hours -= (entry.break_duration || 0) / 60;
      acc[key].total_hours += hours;
      acc[key].total_break_minutes += entry.break_duration || 0;
      acc[key].entries_count += 1;
      return acc;
    }, {});

    return Object.values(grouped) as TimeEntryReport[];
  }, [companyData?.timeEntries, filters]);

  const costReport = useMemo(() => {
    if (!companyData?.costs) return [];

    const filtered = companyData.costs.filter(cost => {
      const costDate = new Date(cost.date);
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      return costDate >= startDate &&
             costDate <= endDate &&
             (!filters.projectId || cost.project_id === filters.projectId);
    });

    const grouped = filtered.reduce((acc: Record<string, any>, cost: any) => {
      const key = `${cost.projects?.name || 'Onbekend'}-${cost.category}`;
      if (!acc[key]) {
        acc[key] = {
          project_name: cost.projects?.name || 'Onbekend',
          category: cost.category,
          total_amount: 0,
          entries_count: 0
        };
      }
      acc[key].total_amount += Number(cost.amount);
      acc[key].entries_count += 1;
      return acc;
    }, {});

    return Object.values(grouped) as CostReport[];
  }, [companyData?.costs, filters]);

  const kilometerReport = useMemo(() => {
    if (!companyData?.kilometers) return [];

    const filtered = companyData.kilometers.filter(km => {
      const kmDate = new Date(km.date);
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      return kmDate >= startDate &&
             kmDate <= endDate &&
             (!filters.projectId || km.project_id === filters.projectId) &&
             (!filters.employeeId || km.employee_id === filters.employeeId);
    });

    const grouped = filtered.reduce((acc: Record<string, any>, km: any) => {
      const key = `${km.employees?.name || 'Onbekend'}-${km.projects?.name || 'Onbekend'}`;
      if (!acc[key]) {
        acc[key] = {
          employee_name: km.employees?.full_name || 'Onbekend',
          project_name: km.projects?.name || 'Onbekend',
          total_distance: 0,
          total_cost: 0,
          entries_count: 0
        };
      }
      const distance = Number(km.distance);
      const rate = Number(km.rate || 0.23);
      acc[key].total_distance += distance;
      acc[key].total_cost += distance * rate;
      acc[key].entries_count += 1;
      return acc;
    }, {});

    return Object.values(grouped) as KilometerReport[];
  }, [companyData?.kilometers, filters]);

  // Helper functions
  const formatHours = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const exportToCsv = (data: any[], filename: string) => {
    if (!data || data.length === 0) {
      toast('Geen data om te exporteren.', {
        icon: '⚠️',
        style: {
          borderRadius: '10px',
          background: '#fef9c3',
          color: '#ca8a04',
        },
      });
      return;
    }
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
      }).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${filters.startDate}_tot_${filters.endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast('CSV succesvol geëxporteerd!', {
      icon: '✅',
      style: {
        borderRadius: '10px',
        background: '#dcfce7',
        color: '#16a34a',
      },
    });
  };

  // Laad- en foutafhandeling
  const isLoading = isLoadingCompanyId || isLoadingCompanyData;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-300">Rapporten laden...</span>
      </div>
    );
  }

  if (isError || !companyId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-red-800 dark:text-red-200">
                Geen toegang
              </h3>
              <p className="text-red-600 dark:text-red-300 mt-1">
                Geen bedrijf gevonden of er is een fout opgetreden. Neem contact op met de beheerder.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center mb-8">
        <ChartBarIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Rapporten</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Overzicht van uren, kosten en kilometers
          </p>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
        <div className="flex items-center mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Startdatum</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Einddatum</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project</label>
            <select
              value={filters.projectId || ''}
              onChange={(e) => setFilters({...filters, projectId: e.target.value || undefined})}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Alle projecten</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Medewerker</label>
            <select
              value={filters.employeeId || ''}
              onChange={(e) => setFilters({...filters, employeeId: e.target.value || undefined})}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Alle medewerkers</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>{employee.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Reports */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Time Entries Report */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ClockIcon className="h-6 w-6 text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Uren Rapport</h3>
              </div>
              <button
                onClick={() => exportToCsv(timeReport, 'uren-rapport')}
                className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                Export CSV
              </button>
            </div>
          </div>
          <div className="p-6">
            {timeReport.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {timeReport.map((entry, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{entry.employee_name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{entry.project_name}</p>
                      </div>
                      <span className="text-lg font-bold text-blue-600">{formatHours(entry.total_hours)}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {entry.entries_count} registraties • {Math.round(entry.total_break_minutes)} min pauze
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                Geen uren gevonden voor deze periode
              </p>
            )}
          </div>
        </div>
        {/* Costs Report */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CurrencyEuroIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kosten Rapport</h3>
              </div>
              <button
                onClick={() => exportToCsv(costReport, 'kosten-rapport')}
                className="flex items-center px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                Export CSV
              </button>
            </div>
          </div>
          <div className="p-6">
            {costReport.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {costReport.map((entry, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{entry.project_name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{entry.category}</p>
                      </div>
                      <span className="text-lg font-bold text-green-600">{formatCurrency(entry.total_amount)}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{entry.entries_count} registraties</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                Geen kosten gevonden voor deze periode
              </p>
            )}
          </div>
        </div>
        {/* Kilometers Report */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <TruckIcon className="h-6 w-6 text-orange-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kilometers Rapport</h3>
              </div>
              <button
                onClick={() => exportToCsv(kilometerReport, 'kilometers-rapport')}
                className="flex items-center px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                Export CSV
              </button>
            </div>
          </div>
          <div className="p-6">
            {kilometerReport.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {kilometerReport.map((entry, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{entry.employee_name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{entry.project_name}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-orange-600">{entry.total_distance.toFixed(1)} km</span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{formatCurrency(entry.total_cost)}</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{entry.entries_count} registraties</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                Geen kilometers gevonden voor deze periode
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Summary */}
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Periode Samenvatting ({new Date(filters.startDate).toLocaleDateString('nl-NL')} tot {new Date(filters.endDate).toLocaleDateString('nl-NL')})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {formatHours(timeReport.reduce((sum, entry) => sum + entry.total_hours, 0))}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Totale uren</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(costReport.reduce((sum, entry) => sum + entry.total_amount, 0))}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Totale kosten</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {kilometerReport.reduce((sum, entry) => sum + entry.total_distance, 0).toFixed(1)} km
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Totale kilometers</div>
          </div>
        </div>
      </div>
    </div>
  );
}